import type { SessionSlot } from '@features/customization-session/types';

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Zap, EyeOff, Images, FileText, Download, ListChecks } from 'lucide-react';

import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/skeleton';
import { StatusLabel } from '@shared/components/status-label';
import { EmptyContent } from '@shared/components/empty-content';
import { WarehouseBreadcrumbs } from '@shared/components/breadcrumbs';

import { JigGrid } from '@features/customization-session/components/jig-grid';
import { SessionMeta } from '@features/customization-session/components/session-meta';
import { SessionPanel } from '@features/customization-session/components/session-panel';
import { useShowVisuals } from '@features/customization-session/hooks/use-show-visuals';
import { LocationSlots } from '@features/customization-session/components/location-slots';
import { useSessionActions } from '@features/customization-session/hooks/use-session-actions';
import { SessionStatusChip } from '@features/customization-session/components/session-status-chip';
import { useGetSessionQuery } from '@features/customization-session/api/customization-session-api';
import { RemoveProductDialog } from '@features/customization-session/components/remove-product-dialog';

const PAGE_TITLE = 'Session de personnalisation';
const REFRESH_INTERVAL_MS = 120_000;

/** Long labels wrap instead of running out of the panel: the button grows in height rather than overflowing. */
const WRAP = 'h-auto min-h-12 w-full justify-center py-2 text-center whitespace-normal';

const ActionButton = ({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) => (
  <Button className={WRAP} disabled={disabled} onClick={onClick} size="lg">
    {label}
  </Button>
);

/** One session and the actions of its current step: the products on the left, the step and its actions on the right. */
export const SessionDetailView = () => {
  const id = Number(useParams().id);
  const navigate = useNavigate();
  const {
    data: detail,
    isLoading,
    isError,
  } = useGetSessionQuery(id, {
    skip: !Number.isInteger(id),
    pollingInterval: REFRESH_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });
  const actions = useSessionActions(id);
  const [toRemove, setToRemove] = useState<SessionSlot | null>(null);
  const [showVisuals, toggleVisuals] = useShowVisuals();

  const breadcrumbs = [
    { name: 'Atelier', href: '/workshop' },
    { name: 'Sessions', href: '/workshop/sessions' },
    { name: `N°${Number.isInteger(id) ? id : ''}` },
  ];

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        <WarehouseBreadcrumbs breadcrumbs={breadcrumbs} page={PAGE_TITLE} />
        <EmptyContent
          description="La session n'existe pas ou n'a pas pu être chargée."
          icon={<ListChecks />}
          title="Session introuvable"
        />
      </div>
    );
  }

  if (isLoading || !detail) {
    return (
      <div className="flex flex-col gap-6">
        <WarehouseBreadcrumbs breadcrumbs={breadcrumbs} page={PAGE_TITLE} />
        <Skeleton className="h-80 rounded-lg" />
      </div>
    );
  }

  const { summary, slots } = detail;
  const allLoaded = slots.every((slot) => slot.loaded);
  const allStored = slots.every((slot) => slot.stored);
  const hasCustomizations = slots.some((slot) => slot.customizations.length > 0);
  const hasVisuals = slots.some((slot) => slot.medias.length > 0);
  /** Products with a personalization at the shop but no PDF yet: they will not be in the zip. */
  const withoutPdf = slots.filter(
    (slot) => slot.customizations.length > 0 && slot.customizations.every((customization) => !customization.pdfUrl)
  );
  const hasPdf = slots.some((slot) => slot.customizations.some((customization) => customization.pdfUrl));
  const withoutPdfNumbers = withoutPdf.map((slot) => slot.number).join(', ');

  return (
    <div className="mx-auto flex w-full max-w-400 flex-col gap-6">
      <WarehouseBreadcrumbs breadcrumbs={breadcrumbs} page={PAGE_TITLE} />

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold md:text-2xl">{summary.family}</h1>
            {summary.express && (
              <StatusLabel color="error" startIcon={<Zap />} variant="soft">
                Express
              </StatusLabel>
            )}
          </div>
          <span className="text-base text-muted-foreground">Session N°{summary.id}</span>
        </div>
        {hasVisuals && (
          <Button aria-pressed={showVisuals} onClick={toggleVisuals} variant={showVisuals ? 'soft' : 'outline'}>
            {showVisuals ? <EyeOff /> : <Images />}
            {showVisuals ? 'Masquer les visuels' : 'Afficher les visuels'}
          </Button>
        )}
        {hasCustomizations && summary.status !== 'pdf' && (
          <Button disabled={actions.busy} onClick={() => actions.fetchMissingPdfs()} variant="outline">
            <FileText />
            Récupérer les PDF manquants
          </Button>
        )}
        {hasPdf && summary.status !== 'export' && (
          <Button disabled={actions.busy} onClick={() => actions.download()} variant="outline">
            <Download />
            Télécharger l&apos;export
          </Button>
        )}
        <SessionStatusChip status={summary.status} />
      </div>

      <SessionMeta summary={summary} />

      <div className="grid items-start gap-6" style={{ gridTemplateColumns: 'minmax(0, 1fr) 360px' }}>
        <div>
          {summary.status === 'to_load' && (
            <LocationSlots
              disabled={actions.busy}
              onFetchPdf={actions.fetchPdf}
              onRemove={setToRemove}
              onToggle={(slot) => actions.load([slot.number], !slot.loaded)}
              showVisuals={showVisuals}
              slots={slots}
              step="load"
            />
          )}
          {summary.status === 'customized' && (
            <LocationSlots
              disabled={actions.busy}
              onFetchPdf={actions.fetchPdf}
              onToggle={(slot) => actions.store([slot.number], !slot.stored)}
              showVisuals={showVisuals}
              slots={slots}
              step="store"
            />
          )}
          {(summary.status === 'pdf' || summary.status === 'export') && (
            <JigGrid
              disabled={actions.busy}
              onFetchPdf={actions.fetchPdf}
              onRemove={setToRemove}
              showVisuals={showVisuals}
              slots={slots}
            />
          )}
          {(summary.status === 'ready' || summary.status === 'in_progress') && (
            <JigGrid
              disabled={actions.busy}
              onFetchPdf={actions.fetchPdf}
              onRemove={setToRemove}
              showVisuals={showVisuals}
              slots={slots}
            />
          )}
          {summary.status === 'stored' && (
            <JigGrid disabled={actions.busy} onFetchPdf={actions.fetchPdf} showVisuals={showVisuals} slots={slots} />
          )}
        </div>

        {summary.status === 'to_load' && (
          <SessionPanel
            instruction="Récupérez chaque produit dans son emplacement et posez-le sur le numéro indiqué du gabarit."
            severity="info"
            summary={summary}
          >
            <ActionButton
              disabled={actions.busy || allLoaded}
              label="Tous les produits sont dans le gabarit"
              onClick={() => actions.load()}
            />
          </SessionPanel>
        )}

        {summary.status === 'pdf' && (
          <SessionPanel
            instruction={
              withoutPdf.length > 0
                ? `Récupérez les PDF de personnalisation chez la boutique. Sans PDF : produit${withoutPdf.length > 1 ? 's' : ''} ${withoutPdfNumbers}.`
                : "Les PDF de personnalisation sont récupérés. Passez à l'export."
            }
            severity={withoutPdf.length > 0 ? 'warning' : 'info'}
            summary={summary}
          >
            <ActionButton
              disabled={actions.busy || withoutPdf.length === 0}
              label="Récupérer les PDF manquants"
              onClick={() => actions.fetchMissingPdfs()}
            />
            <Button className={WRAP} disabled={actions.busy} onClick={() => actions.confirmPdf()} variant="outline">
              Continuer vers l&apos;export
            </Button>
          </SessionPanel>
        )}

        {summary.status === 'export' && (
          <SessionPanel
            instruction={
              withoutPdf.length > 0
                ? `Téléchargez les fichiers de production. Produit${withoutPdf.length > 1 ? 's' : ''} ${withoutPdfNumbers} sans PDF : absent${withoutPdf.length > 1 ? 's' : ''} du zip.`
                : 'Téléchargez les fichiers de production, puis placez le gabarit dans la machine.'
            }
            severity={withoutPdf.length > 0 ? 'warning' : 'info'}
            summary={summary}
          >
            <Button className={WRAP} disabled={actions.busy} onClick={() => actions.download()} size="lg">
              <Download />
              Télécharger le zip
            </Button>
            <Button className={WRAP} disabled={actions.busy} onClick={() => actions.confirmExport()} variant="outline">
              Continuer vers la machine
            </Button>
          </SessionPanel>
        )}

        {summary.status === 'ready' && (
          <SessionPanel
            instruction="Le gabarit est complet. Placez-le dans la machine, puis lancez la personnalisation."
            severity="info"
            summary={summary}
          >
            <ActionButton disabled={actions.busy} label="Lancer la personnalisation" onClick={() => actions.start()} />
            <Button
              className={WRAP}
              disabled={actions.busy}
              onClick={() => actions.load(undefined, false)}
              variant="ghost"
            >
              Un produit n&apos;est pas dans le gabarit
            </Button>
          </SessionPanel>
        )}

        {summary.status === 'in_progress' && (
          <SessionPanel
            instruction={`Personnalisation en cours sur ${summary.machine}.`}
            severity="warning"
            summary={summary}
          >
            <ActionButton disabled={actions.busy} label="Personnalisation terminée" onClick={() => actions.finish()} />
          </SessionPanel>
        )}

        {summary.status === 'customized' && (
          <SessionPanel
            instruction="Retirez les produits du gabarit et rangez-les dans leur emplacement d'origine."
            severity="info"
            summary={summary}
          >
            <ActionButton
              disabled={actions.busy || allStored}
              label="Tous les produits sont rangés"
              onClick={() => actions.store()}
            />
          </SessionPanel>
        )}

        {summary.status === 'stored' && (
          <SessionPanel
            instruction="Session terminée : tous les produits sont rangés, il n'y a plus aucune action à faire."
            severity="success"
            summary={summary}
          >
            <Button className={WRAP} onClick={() => navigate('/workshop/sessions')} size="lg" variant="outline">
              Retour aux sessions
            </Button>
          </SessionPanel>
        )}
      </div>
      {toRemove && (
        <RemoveProductDialog
          busy={actions.busy}
          onClose={() => setToRemove(null)}
          onConfirm={async () => {
            const emptied = await actions.remove(toRemove.number);
            setToRemove(null);
            if (emptied) {
              navigate('/workshop/sessions');
            }
          }}
          slot={toRemove}
        />
      )}
    </div>
  );
};
